import React from "react";
import classnames from "classnames";
import Loader from "../../../components/Loader/Loader";
import { notification } from "../../../utils/utility";
import {
  TEMPLATE_TYPE_LABELS,
  USER_TYPE_LABELS,
  fetchSuperAdminEmailTemplates,
  saveSuperAdminEmailTemplate,
  fetchSuperAdminSmsTemplates,
  saveSuperAdminSmsTemplate,
} from "../../../actions/superAdminTemplateActions";
import "../../../assets/scss/pages/users.scss";
import "./NotificationTemplates.css";

class NotificationTemplates extends React.Component {
  state = {
    channel: "email",
    userType: "C",
    templates: [],
    tags: [],
    drafts: {},
    loading: true,
    savingId: null,
    selectedId: null,
    tagsExpanded: false,
    successMsg: "",
  };

  componentDidMount() {
    this.loadTemplates("email");
  }

  loadTemplates = async (channelOverride) => {
    const channel = channelOverride || this.state.channel;
    this.setState({ loading: true });

    try {
      const res =
        channel === "email"
          ? await fetchSuperAdminEmailTemplates()
          : await fetchSuperAdminSmsTemplates();

      const templates = res.data.templates || [];
      const drafts = {};
      templates.forEach((template) => {
        drafts[template.id] = this.templateDraftFromRow(template, channel);
      });

      const filtered = templates.filter((t) => t.user_type === this.state.userType);
      const selectedId =
        filtered.find((t) => t.id === this.state.selectedId)?.id ||
        filtered[0]?.id ||
        null;

      this.setState({
        templates,
        tags: res.data.tags || [],
        drafts,
        loading: false,
        selectedId,
      });
    } catch (err) {
      this.setState({ loading: false });
      notification.error("Failed to load templates.");
    }
  };

  templateDraftFromRow = (template, channel) => {
    if (channel === "email") {
      return {
        subject: template.subject || "",
        message: template.message || template.default_message || "",
      };
    }

    return {
      sms_subject: template.sms_subject || "",
      sms_message: template.sms_message || template.default_message || "",
    };
  };

  handleChannelChange = (channel) => {
    this.setState({ channel, userType: "C", selectedId: null }, () =>
      this.loadTemplates(channel)
    );
  };

  handleUserTypeChange = (userType) => {
    const filtered = this.state.templates.filter((t) => t.user_type === userType);
    this.setState({
      userType,
      selectedId: filtered[0]?.id || null,
    });
  };

  handleDraftChange = (id, field, value) => {
    this.setState((prev) => ({
      drafts: {
        ...prev.drafts,
        [id]: {
          ...prev.drafts[id],
          [field]: value,
        },
      },
    }));
  };

  copyTagToClipboard = (tag) => {
    const text = `{{${tag}}}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      notification.success(`Copied ${text}`);
    }
  };

  handleSave = async () => {
    const template = this.state.templates.find((t) => t.id === this.state.selectedId);
    if (!template) return;

    const draft = this.state.drafts[template.id];
    this.setState({ savingId: template.id });

    try {
      if (this.state.channel === "email") {
        await saveSuperAdminEmailTemplate({
          id: template.id,
          subject: draft.subject,
          message: draft.message,
        });
      } else {
        await saveSuperAdminSmsTemplate({
          id: template.id,
          sms_subject: draft.sms_subject,
          sms_message: draft.sms_message,
        });
      }

      this.setState({ successMsg: "Template saved successfully." });
      setTimeout(() => this.setState({ successMsg: "" }), 3000);
      await this.loadTemplates();
    } catch (err) {
      notification.error("Failed to save template.");
    } finally {
      this.setState({ savingId: null });
    }
  };

  filteredTemplates = () =>
    this.state.templates.filter((t) => t.user_type === this.state.userType);

  getTemplateTitle = (template) => {
    if (this.state.channel === "email") {
      return template.subject || "Untitled email";
    }
    return template.sms_subject || "Untitled SMS";
  };

  getTemplateTypeKey = (template) =>
    this.state.channel === "email"
      ? template.template_type
      : template.sms_template_type;

  renderEditor = () => {
    const template = this.state.templates.find((t) => t.id === this.state.selectedId);
    if (!template) {
      return (
        <div className="nt-empty">
          Select a template from the list to edit its content.
        </div>
      );
    }

    const draft = this.state.drafts[template.id] || {};
    const isEmail = this.state.channel === "email";

    return (
      <div className="nt-editor-panel">
        <div className="nt-editor-header">
          <div>
            <h4>{this.getTemplateTitle(template)}</h4>
            <p>
              {USER_TYPE_LABELS[template.user_type]} ·{" "}
              {TEMPLATE_TYPE_LABELS[this.getTemplateTypeKey(template)] ||
                this.getTemplateTypeKey(template)}
            </p>
          </div>
          <button
            type="button"
            className="nt-save-btn"
            disabled={this.state.savingId === template.id}
            onClick={this.handleSave}
          >
            {this.state.savingId === template.id ? "Saving..." : "Save changes"}
          </button>
        </div>

        {isEmail ? (
          <>
            <div className="nt-field">
              <label>Email subject</label>
              <input
                type="text"
                value={draft.subject || ""}
                onChange={(e) =>
                  this.handleDraftChange(template.id, "subject", e.target.value)
                }
                placeholder="Enter email subject line"
              />
            </div>
            <div className="nt-field">
              <label>HTML body</label>
              <textarea
                value={draft.message || ""}
                onChange={(e) =>
                  this.handleDraftChange(template.id, "message", e.target.value)
                }
                placeholder="Enter HTML email content..."
              />
            </div>
          </>
        ) : (
          <>
            <div className="nt-field">
              <label>SMS title</label>
              <input
                type="text"
                value={draft.sms_subject || ""}
                onChange={(e) =>
                  this.handleDraftChange(template.id, "sms_subject", e.target.value)
                }
                placeholder="Internal template title"
              />
            </div>
            <div className="nt-field">
              <label>Message text</label>
              <textarea
                value={draft.sms_message || ""}
                onChange={(e) =>
                  this.handleDraftChange(template.id, "sms_message", e.target.value)
                }
                placeholder="Enter SMS message..."
                style={{ minHeight: 180 }}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  render() {
    const { channel, userType, loading, tagsExpanded, successMsg } = this.state;
    const items = this.filteredTemplates();
    const userTypes = channel === "email" ? ["C", "A", "S"] : ["C", "A"];

    return (
      <div className="eventdetailsaddbox rd_noshadow nt-page">
        <Loader isShowLoader={loading} />

        <div className="rd_supertoptwocom">
          <div className="boxheader rd_floatingheaderthig">
            <div className="rd_inputselectheader">
              <div className="rd_selectheaderrdt2 rd_selectheaderrdt2profile">
                <h3>
                  <strong>Notification Templates</strong>
                </h3>
                <p className="nt-subtitle">
                  Manage platform default email and SMS messages. New subscribers
                  receive copies of these templates when their account is created.
                </p>
              </div>
            </div>
          </div>
        </div>

        {successMsg && <div className="nt-success-banner">{successMsg}</div>}

        <div className="nt-channel-tabs">
          <button
            type="button"
            className={classnames({ active: channel === "email" })}
            onClick={() => this.handleChannelChange("email")}
          >
            Email templates
          </button>
          <button
            type="button"
            className={classnames({ active: channel === "sms" })}
            onClick={() => this.handleChannelChange("sms")}
          >
            SMS templates
          </button>
        </div>

        <div className="nt-audience-tabs">
          {userTypes.map((type) => (
            <button
              key={type}
              type="button"
              className={classnames({ active: userType === type })}
              onClick={() => this.handleUserTypeChange(type)}
            >
              {USER_TYPE_LABELS[type]}
            </button>
          ))}
        </div>

        {this.state.tags.length > 0 && (
          <div className="nt-tags-panel">
            <button
              type="button"
              className="nt-tags-toggle"
              onClick={() => this.setState({ tagsExpanded: !tagsExpanded })}
            >
              <strong>Merge tags</strong>
              <span>{tagsExpanded ? "Hide" : "Show"} placeholders</span>
            </button>
            {tagsExpanded && (
              <div className="nt-tags-body">
                <p>
                  Click a tag to copy it. Placeholders are replaced with real
                  booking data when notifications are sent.
                </p>
                <div className="nt-tag-grid">
                  {this.state.tags.map((tag) => (
                    <button
                      key={tag.tag}
                      type="button"
                      className="nt-tag-chip"
                      title={tag.name}
                      onClick={() => this.copyTagToClipboard(tag.tag)}
                    >
                      {`{{${tag.tag}}}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && items.length === 0 ? (
          <div className="nt-empty">No templates found for this audience.</div>
        ) : (
          <div className="nt-layout">
            <aside className="nt-sidebar">
              <div className="nt-sidebar-header">
                {USER_TYPE_LABELS[userType]} templates
              </div>
              <ul className="nt-template-list">
                {items.map((template) => (
                  <li key={template.id}>
                    <button
                      type="button"
                      className={classnames({
                        active: this.state.selectedId === template.id,
                      })}
                      onClick={() => this.setState({ selectedId: template.id })}
                    >
                      <span className="nt-item-title">
                        {this.getTemplateTitle(template)}
                      </span>
                      <span className="nt-item-meta">
                        {TEMPLATE_TYPE_LABELS[this.getTemplateTypeKey(template)] ||
                          this.getTemplateTypeKey(template)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {this.renderEditor()}
          </div>
        )}
      </div>
    );
  }
}

export default NotificationTemplates;
